import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './PavanEnterprises.css';
import penLogo from './assets/pen-logo.jpeg';

// ── Logo ─────────────────────────────────────────────────────────────
function LogoMark({ size = 40 }) {
  return (
    <img
      src={penLogo}
      alt="Pavan Enterprises Network"
      className="pen-logo-mark"
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── AI CREATION MODES (hero console) ────────────────────────────────
const MODES = [
  { id: 'image', label: 'Image Ad',     icon: '🖼️', ratio: '1:1',  style: 'Vivid',  prompt: 'Static ad for a vitamin-C serum — glass bottle, citrus splash, 5-star badge, bold claim.' },
  { id: 'video', label: 'Video Ad',     icon: '🎬', ratio: '16:9', style: 'Cinematic', prompt: '15-second video ad for running shoes — speed lines, price reveal, punchy caption.' },
  { id: 'ugc',   label: 'UGC Ad',       icon: '🤳', ratio: '9:16', style: 'Authentic', prompt: 'UGC-style testimonial for a protein bar — creator selfie energy, product tag, TikTok vibe.' },
  { id: 'shot',  label: 'Product Shot', icon: '📸', ratio: '4:5',  style: 'Studio', prompt: 'Studio product shot — matte black bottle, blush backdrop, soft daylight, 4K.' },
  { id: 'logo',  label: 'Logo',         icon: '◆',  ratio: '1:1',  style: 'Minimal', prompt: 'Minimal wordmark for HALO Skincare — modern serif, gold accent, favicon variants.' },
  { id: 'blog',  label: 'Blog Post',    icon: '✍️', ratio: 'Doc',  style: 'Brand voice', prompt: 'SEO article: "Ceramides vs. retinol for sensitive skin" — 1,200 words in our brand voice.' },
  { id: 'voice', label: 'Brand Voice',  icon: '🗣️', ratio: 'Voice', style: 'Trained', prompt: 'Rewrite our shipping-delay email in brand voice — confident, playful, zero corporate jargon.' },
];

// ── REAL MEDIA — actual photos & video used in place of drawn mockups ──
// Swap any of these for your own brand assets whenever you're ready.
const MEDIA = {
  image: { type: 'image', alt: 'Vitamin C serum bottle product photo',
    src: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?auto=format&fit=crop&w=1000&q=85',
    fallback: 'https://picsum.photos/seed/glowlab-serum/900/900' },
  video: { type: 'video', alt: 'Runner tying shoes and running in motion',
    src: 'https://assets.mixkit.co/videos/preview/mixkit-legs-of-a-woman-running-on-a-treadmill-32809-large.mp4',
    fallback: 'https://assets.mixkit.co/videos/preview/mixkit-womans-feet-walking-on-a-city-street-4652-large.mp4',
    poster: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=900&q=80' },
  ugc: { type: 'image', alt: 'Creator-style selfie portrait',
    src: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=800&q=80',
    fallback: 'https://picsum.photos/seed/glowlab-ugc/800/1000' },
  shot: { type: 'image', alt: 'Studio product photography, dark bottle on blush backdrop',
    src: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80',
    fallback: 'https://picsum.photos/seed/glowlab-shot/800/800' },
  logo: { type: 'image', alt: 'Neutral brand identity backdrop',
    src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80',
    fallback: 'https://picsum.photos/seed/glowlab-logo/800/700' },
  blog: { type: 'image', alt: 'Skincare flatlay editorial photo',
    src: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=800&q=80',
    fallback: 'https://picsum.photos/seed/glowlab-blog/800/500' },
  voice: { type: 'image', alt: 'Notebook and desk, brand voice workspace',
    src: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80',
    fallback: 'https://picsum.photos/seed/glowlab-voice/900/400' },
};

// ── Brand voice trait profile used by the Brand Voice artifact ──
const VOICE_TRAITS = [
  { name: 'Playful',   pct: 82 },
  { name: 'Confident', pct: 76 },
  { name: 'Warm',      pct: 68 },
  { name: 'Bold',      pct: 71 },
];

// ── Real-media background: fades in a photo/video, shows a soft
//    loading shimmer first, and never breaks layout if a URL 404s ──
function MediaBg({ media, scrimVariant, showScrim = true }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const currentSrc = usedFallback ? (media.fallback || media.src) : media.src;

  const handleError = () => {
    if (!usedFallback && media.fallback) {
      setUsedFallback(true);
    } else {
      setErrored(true);
    }
  };

  return (
    <div className="pen-media-frame">
      {media.type === 'video' ? (
        <video
          className={loaded ? 'loaded' : ''}
          style={errored ? { opacity: 0 } : undefined}
          src={currentSrc}
          poster={media.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onCanPlay={() => setLoaded(true)}
          onError={handleError}
        />
      ) : (
        <img
          className={loaded ? 'loaded' : ''}
          style={errored ? { opacity: 0 } : undefined}
          src={currentSrc}
          alt={media.alt || ''}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
      {showScrim && <div className={`pen-media-scrim ${scrimVariant || ''}`} />}
      {!loaded && !errored && <div className="pen-media-loading">✨ Loading media…</div>}
    </div>
  );
}

const TYPE_SPEED = 26;
const GENERATE_TIME = 1500;
const HOLD_TIME = 3600;
const SCENE_COUNT = 5;

const CAPABILITIES = [
  { icon: '🖼️', title: 'Image Ads', desc: 'Scroll-stopping statics for Meta, Google, and Pinterest — generated in your brand style, sized for every placement.', tag: 'Meta · Google · Display' },
  { icon: '🎬', title: 'Video & Commercial Ads', desc: 'Script-to-screen AI video ads and polished commercials. Hooks, cuts, captions, end cards — done.', tag: 'TikTok · Reels · YouTube' },
  { icon: '🤳', title: 'UGC-Style Ads', desc: 'Authentic creator-style content that converts — testimonial angles, unboxings, and lo-fi hooks on demand.', tag: 'TikTok · Reels' },
  { icon: '📸', title: 'Product Shots', desc: 'Studio-quality product photography from a single upload. Any scene, any lighting, no photoshoot.', tag: 'PDP · Amazon · Ads' },
  { icon: '✍️', title: 'Blogs & Articles', desc: 'SEO-ready blogs, landing copy, and email flows — written in a brand voice trained on you.', tag: 'SEO · Email · Landing' },
  { icon: '◆', title: 'Logo & Brand Identity', desc: 'Logos, palettes, and full identity systems. Your brand voice becomes a model every asset obeys.', tag: 'Identity · Guidelines' },
  { icon: '🔁', title: 'Text ↔ Image', desc: 'Describe it, see it. Upload it, describe it. Idea-to-visual and visual-to-copy in seconds.', tag: 'Generate · Describe' },
  { icon: '🗣️', title: 'Brand Voice Engine', desc: 'We train an AI model on your tone, claims, and customers — every asset sounds unmistakably you.', tag: 'Trained on you' },
];

const STEPS = [
  { n: '1', title: 'Describe your idea', desc: 'Type a prompt, drop a product photo, or pick a template. Plain English is enough.' },
  { n: '2', title: 'AI creates in your brand', desc: 'Our models generate ads, videos, shots, and copy in your trained brand voice and style.' },
  { n: '3', title: 'Refine, export, launch', desc: 'Iterate with one click, get human polish when you want it, and ship to every channel.' },
];

const WHY = [
  { title: 'Built for modern DTC', desc: 'Playbooks tuned to Meta, TikTok, and Amazon — the channels where fast-growing brands actually win.' },
  { title: 'Kill creative fatigue', desc: 'Fresh ad variations every week, so your CAC stops climbing when your creative goes stale.' },
  { title: 'AI speed, human polish', desc: 'Machines do the volume. Our creative team does the taste. You get both, in days not weeks.' },
  { title: 'One platform, every asset', desc: 'Ads, video, UGC, product shots, blogs, logos — one subscription replaces five vendors.' },
];

const TICKER = [
  'Image Ads', 'AI Video Ads', 'UGC Style', 'Product Shots', 'Commercials',
  'Blog Writing', 'Logo Design', 'Brand Voice', 'Text to Image', 'Image to Text',
];

const CATEGORIES = [
  'Beauty & Personal Care', 'Apparel & Fashion', 'Food & Beverage',
  'Health & Supplements', 'Home & Lifestyle', 'Consumer Electronics',
  'Jewelry & Accessories', 'Pet Products', 'Baby & Kids', 'Other',
];

// ════════════════════════════════════════════════════════════════════
export default function PavanEnterprises() {
  const [menuOpen, setMenuOpen] = useState(false);

  // ── SCROLL-MORPHING BACKGROUND ──
  const [scene, setScene] = useState(0);
  const [renderPulse, setRenderPulse] = useState(0);
  useEffect(() => {
    const sections = document.querySelectorAll('[data-scene]');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const idx = Number(e.target.dataset.scene);
          setScene((prev) => {
            if (prev !== idx) setRenderPulse((p) => p + 1);
            return idx;
          });
        });
      },
      { rootMargin: '-42% 0px -42% 0px', threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  // ── Hero console state machine: typing → generating → reveal ──
  const [modeIdx, setModeIdx] = useState(0);
  const [phase, setPhase] = useState('typing');
  const [typed, setTyped] = useState('');
  const timerRef = useRef(null);
  const mode = MODES[modeIdx];

  useEffect(() => {
    clearTimeout(timerRef.current);
    const prompt = MODES[modeIdx].prompt;

    if (phase === 'typing') {
      if (typed.length < prompt.length) {
        timerRef.current = setTimeout(
          () => setTyped(prompt.slice(0, typed.length + 1)),
          TYPE_SPEED
        );
      } else {
        timerRef.current = setTimeout(() => setPhase('generating'), 500);
      }
    } else if (phase === 'generating') {
      timerRef.current = setTimeout(() => setPhase('reveal'), GENERATE_TIME);
    } else if (phase === 'reveal') {
      timerRef.current = setTimeout(() => {
        setTyped('');
        setPhase('typing');
        setModeIdx((i) => (i + 1) % MODES.length);
      }, HOLD_TIME);
    }
    return () => clearTimeout(timerRef.current);
  }, [phase, typed, modeIdx]);

  const jumpToMode = (i) => {
    clearTimeout(timerRef.current);
    setModeIdx(i);
    setTyped('');
    setPhase('typing');
  };

  // ── Scroll reveal ──
  useEffect(() => {
    const els = document.querySelectorAll('.pen-reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('pen-vis'); obs.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const fn = () => setMenuOpen(false);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // ── Console tilt / cursor glow (interactive) ──
  const consoleRef = useRef(null);
  const handleConsoleMove = useCallback((e) => {
    const el = consoleRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (0.5 - py) * 6;
    const ry = (px - 0.5) * 8;
    el.style.setProperty('--tiltX', `${rx}deg`);
    el.style.setProperty('--tiltY', `${ry}deg`);
    el.style.setProperty('--glowX', `${px * 100}%`);
    el.style.setProperty('--glowY', `${py * 100}%`);
  }, []);
  const handleConsoleLeave = useCallback(() => {
    const el = consoleRef.current;
    if (!el) return;
    el.style.setProperty('--tiltX', `0deg`);
    el.style.setProperty('--tiltY', `0deg`);
  }, []);

  // ── Registration form (backend contract unchanged) ──
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [userName, setUserName] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', industry: '', message: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!form.firstName.trim()) { setSubmitError('Please enter your first name.'); return; }
    if (!form.email.trim())     { setSubmitError('Please enter your email address.'); return; }
    if (!form.phone.trim())     { setSubmitError('Please enter your phone number.'); return; }
    setSubmitting(true);

    const BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 'http://localhost:3001'
      : '';

    try {
      await axios.post(`${BASE}/api/register`, {
        firstName: form.firstName.trim(),
        lastName:  form.lastName.trim(),
        email:     form.email.trim(),
        phone:     form.phone.trim(),
        industry:  form.industry,
        message:   form.message.trim(),
        source:    window.location.href,
      });
      setUserName(form.firstName.trim());
      setSubmitted(true);
      setTimeout(() => scrollTo('pen-register'), 100);
    } catch (err) {
      if (err.response?.status === 409) {
        setSubmitError('This email is already registered! We will contact you shortly.');
      } else if (err.response?.data?.errors) {
        setSubmitError(err.response.data.errors[0]?.msg || 'Please check your details.');
      } else {
        setUserName(form.firstName.trim());
        setSubmitted(true);
        setTimeout(() => scrollTo('pen-register'), 100);
        try {
          const list = JSON.parse(localStorage.getItem('pen_leads') || '[]');
          list.push({ ...form, id: Date.now(), time: new Date().toLocaleString(), offline: true });
          localStorage.setItem('pen_leads', JSON.stringify(list));
        } catch (_) {}
      }
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  // ── Rich generation artifacts — real photos & video, not drawn mockups ──
  const renderArtifact = () => {
    switch (mode.id) {
      case 'image':
        return (
          <div className="pen-art pen-art-image">
            <MediaBg media={MEDIA.image} scrimVariant="top" />
            <div className="pen-ig-head">
              <div className="pen-ig-avatar">G</div>
              <div className="pen-ig-meta">
                <span className="pen-ig-name">glowlab.skin</span>
                <span className="pen-ig-sponsored">Sponsored</span>
              </div>
              <span className="pen-ai-badge">✨ AI</span>
            </div>
            <div className="pen-ig-foot">
              <div className="pen-ig-actions">
                <span>♡</span><span>💬</span><span>↗</span>
                <span className="pen-ig-save">⚑</span>
              </div>
              <div className="pen-ig-likes"><b>12,458</b> likes</div>
              <div className="pen-ig-caption"><b>glowlab.skin</b> Brighter skin in 14 days ✨ — our best-selling Vitamin C serum is back in stock.</div>
              <button className="pen-ig-cta">Shop Now</button>
            </div>
          </div>
        );
      case 'video':
        return (
          <div className="pen-art pen-art-video">
            <div className="pen-video-frame">
              <MediaBg media={MEDIA.video} scrimVariant="top" />
              <i className="pen-speed l1" /><i className="pen-speed l2" /><i className="pen-speed l3" />
              <div className="pen-video-play">▶</div>
              <span className="pen-video-badge">● 0:15</span>
              <span className="pen-video-caption">RUN FASTER. PAY LESS.</span>
              <span className="pen-video-price">$89 TODAY</span>
              <div className="pen-eq"><i /><i /><i /><i /></div>
              <span className="pen-ai-badge">✨ AI Generated</span>
            </div>
            <div className="pen-video-track"><div className="pen-video-prog" /></div>
          </div>
        );
      case 'ugc':
        return (
          <div className="pen-art pen-art-ugc">
            <MediaBg media={MEDIA.ugc} scrimVariant="top" />
            <span className="pen-ai-badge">✨ AI Generated</span>
            <div className="pen-ugc-overlay">
              <div className="pen-ugc-stories"><i className="on" /><i /><i /></div>
              <div className="pen-ugc-top">
                <div className="pen-ugc-avatar">M</div>
                <div>
                  <div className="pen-ugc-name">maya.eats <span className="pen-verified">✓</span></div>
                  <div className="pen-ugc-sub">Sponsored · Original audio</div>
                </div>
              </div>
              <div className="pen-ugc-body">"okay this protein bar actually tastes like a brownie 🤯 not sponsored— ok it is but STILL"</div>
              <div className="pen-ugc-product">🛍️ ChocoFuel Bar · <b>$24.99</b></div>
              <div className="pen-ugc-actions"><span>♥ 12.4k</span><span>💬 318</span><span>↗ 2.1k</span></div>
            </div>
            <span className="pen-ugc-heart h1">♥</span>
            <span className="pen-ugc-heart h2">♥</span>
            <span className="pen-ugc-heart h3">♥</span>
          </div>
        );
      case 'shot':
        return (
          <div className="pen-art pen-art-shot">
            <MediaBg media={MEDIA.shot} scrimVariant="top" />
            <span className="pen-ai-badge">✨ AI Generated</span>
            <span className="pen-shot-tag">4K · STUDIO</span>
            <span className="pen-shot-brand">NOIR</span>
          </div>
        );
      case 'logo':
        return (
          <div className="pen-art pen-art-logo">
            <MediaBg media={MEDIA.logo} scrimVariant="top" />
            <span className="pen-ai-badge">✨ AI Generated</span>
            <div className="pen-logo-glyph">H</div>
            <div className="pen-logo-word">HALO<span> SKINCARE</span></div>
            <div className="pen-logo-swatches"><i /><i /><i /><i /></div>
          </div>
        );
      case 'voice':
        return (
          <div className="pen-art pen-art-voice">
            <div className="pen-voice-cover">
              <MediaBg media={MEDIA.voice} scrimVariant="top" />
              <span className="pen-voice-trained">🧠 Trained on 248 assets</span>
              <span className="pen-voice-cover-label">Your Brand Voice Model</span>
            </div>
            <div className="pen-voice-body">
              <div className="pen-voice-traits">
                {VOICE_TRAITS.map((t) => (
                  <div key={t.name} className="pen-voice-trait-row">
                    <span className="pen-voice-trait-name">{t.name}</span>
                    <div className="pen-voice-trait-bar">
                      <div className="pen-voice-trait-fill" style={{ width: `${t.pct}%` }} />
                    </div>
                    <span className="pen-voice-trait-pct">{t.pct}%</span>
                  </div>
                ))}
              </div>
              <div className="pen-voice-rewrite">
                <span className="pen-voice-before">"We regret to inform you of a shipping delay."</span>
                <span className="pen-voice-after">"Small hiccup — your order's running a few days late. Sorry, we hate that too. ✨"</span>
              </div>
              <span className="pen-voice-tag">✓ Applied to every asset</span>
            </div>
          </div>
        );
      case 'blog':
      default:
        return (
          <div className="pen-art pen-art-blog">
            <div className="pen-blog-cover">
              <MediaBg media={MEDIA.blog} scrimVariant="top" />
              <span className="pen-ai-badge">✨ AI Generated</span>
              <span className="pen-blog-tag">SKINCARE · 6 MIN READ</span>
            </div>
            <div className="pen-blog-body">
              <span className="pen-blog-title">Ceramides vs. Retinol: What Sensitive Skin Actually Needs</span>
              <div className="pen-blog-lines"><i style={{width:'96%'}} /><i style={{width:'88%'}} /><i style={{width:'92%'}} /><i style={{width:'61%'}} /></div>
              <div className="pen-blog-foot">
                <span className="pen-blog-voice">✓ Your brand voice</span>
                <span className="pen-blog-seo">SEO 94</span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="pen">

      {/* ── FIXED MORPHING BACKGROUND ── */}
      <div className="pen-bg" aria-hidden="true">
        {Array.from({ length: SCENE_COUNT }).map((_, i) => (
          <div key={i} className={`pen-scene pen-scene-${i} ${i === scene ? 'live' : ''}`} />
        ))}
        <div key={renderPulse} className={`pen-bg-scan ${renderPulse > 0 ? 'go' : ''}`} />
        <div className={`pen-bg-hud ${renderPulse > 0 ? 'go' : ''}`} key={`hud-${renderPulse}`}>
          <span>◐ rendering scene {String(scene + 1).padStart(2, '0')}/{String(SCENE_COUNT).padStart(2, '0')}</span>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      <div className={`pen-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button className="pen-mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close">✕</button>
        {[['create', 'What You Can Create'], ['how', 'How It Works'], ['why', 'Why PEN'], ['pen-register', 'Start Free']].map(([id, label]) => (
          <a key={id} href={`#${id}`} onClick={() => { setMenuOpen(false); setTimeout(() => scrollTo(id), 50); }}>{label}</a>
        ))}
      </div>

      {/* ── NAV ── */}
      <nav className="pen-nav">
        <div className="pen-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <LogoMark size={40} />
          <div className="pen-logo-text">
            <span className="pen-lt">Pavan Enterprises Network</span>
            <span className="pen-lt-sub">AI Creative Studio</span>
          </div>
        </div>
        <div className="pen-nav-links">
          {[['#create', 'Create'], ['#how', 'How It Works'], ['#why', 'Why PEN']].map(([href, label]) => (
            <a key={href} href={href}>{label}</a>
          ))}
        </div>
        <a href="#pen-register" className="pen-nav-cta">Start Creating Free →</a>
        <button className="pen-hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu"><span/><span/><span/></button>
      </nav>

      {/* ── HERO ── */}
      <section className="pen-hero" data-scene="0">
        <div className="pen-hero-inner">
          <div className="pen-hero-copy">
            <div className="pen-eyebrow"><div className="pen-pulse" />AI Creative Studio for Every Brand</div>
            <h1 className="pen-h1">
              Type an idea.<br /><em>Watch it become</em><br />the ad.
            </h1>
            <p className="pen-hsub">
              Image ads, AI video, UGC-style content, product shots, blogs, and logos —
              generated in <strong>your</strong> brand voice, polished by a human creative team.
              One platform. Every asset. Days, not weeks.
            </p>
            <div className="pen-ctas">
              <a href="#pen-register" className="pen-cta-p">Start Creating Free →</a>
              <a href="#create" className="pen-cta-s">See every mode</a>
            </div>
            <div className="pen-hero-proof">No credit card · Brand voice trained on you · Human polish included</div>
          </div>

          {/* ── STUDIO CONSOLE ── */}
          <div className="pen-console" ref={consoleRef} onMouseMove={handleConsoleMove} onMouseLeave={handleConsoleLeave}>
            <i className="pen-console-aura" />
            <div className="pen-console-head">
              <div className="pen-dots"><i /><i /><i /></div>
              <span className="pen-console-title">PEN Studio — New generation</span>
              <span className="pen-console-model">✨ PEN·V2</span>
            </div>

            <div className="pen-console-modes">
              {MODES.map((m, i) => (
                <button key={m.id} className={`pen-mode-chip ${i === modeIdx ? 'on' : ''}`} onClick={() => jumpToMode(i)}>
                  <span className="pen-mode-ico">{m.icon}</span>{m.label}
                </button>
              ))}
            </div>

            <div className="pen-console-prompt">
              <div className="pen-prompt-toprow">
                <span className="pen-prompt-label">✏️ PROMPT</span>
                <span className="pen-prompt-enhance">✨ Auto-enhanced</span>
              </div>
              <span className="pen-prompt-text">
                {typed}
                <span className={`pen-caret ${phase === 'typing' ? 'blink' : ''}`} />
              </span>
            </div>

            <div className="pen-console-opts">
              <span className="pen-opt">◫ {mode.ratio}</span>
              <span className="pen-opt">🎨 {mode.style}</span>
              <span className="pen-opt">⚡ 4K</span>
              <span className={`pen-opt-gen ${phase === 'generating' ? 'busy' : ''}`}>
                {phase === 'generating' ? '◐ Generating…' : phase === 'reveal' ? '✓ Generated' : '⚡ Generate'}
              </span>
            </div>

            <div className={`pen-canvas ${phase}`}>
              {phase !== 'reveal' && (
                <div className="pen-canvas-idle">
                  {phase === 'generating' ? (
                    <>
                      <div className="pen-gen-bar"><i /></div>
                      <span className="pen-gen-text">Generating {mode.label.toLowerCase()}…</span>
                    </>
                  ) : (
                    <span className="pen-gen-text dim">✨ Waiting for your idea…</span>
                  )}
                </div>
              )}
              {phase === 'generating' && <div className="pen-scanline" />}
              {phase === 'reveal' && renderArtifact()}
            </div>

            <div className="pen-console-foot">
              <span className={`pen-status ${phase}`}>
                {phase === 'typing' ? '● Listening' : phase === 'generating' ? '◐ Creating' : '✓ Ready to launch'}
              </span>
              <span className="pen-console-brand">PEN · AI Mode</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="pen-ticker" aria-hidden="true">
        <div className="pen-ti">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => <span key={i} className="pen-titem">{t}</span>)}
        </div>
      </div>

      {/* ── WHAT YOU CAN CREATE ── */}
      <section className="pen-create" id="create" data-scene="1">
        <div className="pen-wrap">
          <div className="pen-section-head center pen-reveal">
            <div className="pen-chip">✦ One Studio, Every Asset</div>
            <h2 className="pen-section-title">Everything Your Brand Needs.<br /><span>Generated On Demand.</span></h2>
            <p className="pen-ss">
              Stop juggling five agencies and three freelancers. Describe what you need —
              PEN creates it in your brand voice, ready for every channel.
            </p>
          </div>
          <div className="pen-cap-grid">
            {CAPABILITIES.map((c, i) => (
              <div key={i} className={`pen-cap pen-reveal pen-d${i % 4}`}>
                <span className="pen-cap-icon">{c.icon}</span>
                <div className="pen-cap-title">{c.title}</div>
                <div className="pen-cap-desc">{c.desc}</div>
                <span className="pen-cap-tag">{c.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="pen-how" id="how" data-scene="2">
        <div className="pen-wrap">
          <div className="pen-section-head center pen-reveal">
            <div className="pen-chip">How It Works</div>
            <h2 className="pen-section-title">Idea → Art in <span>Three Moves</span></h2>
          </div>
          <div className="pen-steps">
            {STEPS.map((s, i) => (
              <div key={i} className={`pen-step pen-reveal pen-d${i}`}>
                <div className="pen-step-n">{s.n}</div>
                <div className="pen-step-t">{s.title}</div>
                <div className="pen-step-d">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY PEN ── */}
      <section className="pen-why" id="why" data-scene="3">
        <div className="pen-wrap pen-why-inner">
          <div className="pen-reveal">
            <div className="pen-chip">Why Choose PEN</div>
            <h2 className="pen-section-title">Creative That Keeps Up<br /><span>With Your Ad Account.</span></h2>
            <p className="pen-ss">
              Fast-growing brands burn out creative every 2–3 weeks. Agencies take a month per batch.
              PEN gives you agency-quality assets at AI speed — so testing never stops.
            </p>
          </div>
          <div className="pen-wcards">
            {WHY.map((w, i) => (
              <div key={i} className={`pen-wc pen-reveal pen-d${i}`}>
                <div className="pen-wc-dot" />
                <div>
                  <div className="pen-wct">{w.title}</div>
                  <div className="pen-wcd">{w.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REGISTER ── */}
      <div className="pen-fw" id="pen-register" data-scene="4">
        <div className="pen-wrap pen-fin">
          <div className="pen-reveal">
            <div className="pen-chip">Start Free</div>
            <h2 className="pen-section-title">Your First Campaign<br /><span>Is One Prompt Away.</span></h2>
            <p className="pen-ss" style={{ marginBottom: 8 }}>
              Tell us about your brand. We'll set up your AI workspace, train your brand voice,
              and get back to you within 24 hours with your first concepts.
            </p>
            <div className="pen-flist">
              {[
                ['⚡', 'Response within 24 business hours'],
                ['🧠', 'Brand voice model trained on your brand'],
                ['🎯', 'Free creative strategy consultation'],
                ['🔒', '100% confidential — your data stays private'],
              ].map(([icon, text], i) => (
                <div key={i} className="pen-fi"><div className="pen-fii">{icon}</div>{text}</div>
              ))}
            </div>
          </div>

          <div className="pen-fcard pen-reveal pen-d1">
            {!submitted ? (
              <>
                <div className="pen-fct">Create Your Studio Account</div>
                <div className="pen-fcs">Fill in your details — we'll be in touch within 24 hours.</div>
                {submitError && <div className="pen-err">{submitError}</div>}
                <form onSubmit={handleSubmit} noValidate>
                  <div className="pen-frow">
                    <div className="pen-field">
                      <label>First Name *</label>
                      <input value={form.firstName} onChange={set('firstName')} placeholder="First name" required />
                    </div>
                    <div className="pen-field">
                      <label>Last Name</label>
                      <input value={form.lastName} onChange={set('lastName')} placeholder="Last name" />
                    </div>
                  </div>
                  <div className="pen-field">
                    <label>Work Email *</label>
                    <input type="email" value={form.email} onChange={set('email')} placeholder="you@yourbrand.com" required />
                  </div>
                  <div className="pen-field">
                    <label>Phone Number *</label>
                    <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 (415) 555-0134" required />
                  </div>
                  <div className="pen-field">
                    <label>Product Category</label>
                    <select value={form.industry} onChange={set('industry')}>
                      <option value="">Select your product category...</option>
                      {CATEGORIES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="pen-field">
                    <label>What Do You Want To Create?</label>
                    <textarea rows={4} value={form.message} onChange={set('message')} placeholder="e.g. We're a supplement brand launching on TikTok Shop — we need UGC ads, product shots, and a landing page…" />
                  </div>
                  <button type="submit" className="pen-fsub" disabled={submitting}>
                    {submitting ? 'Creating your account…' : 'Start Creating Free →'}
                  </button>
                  <p className="pen-fnote">🔒 Your details are safe. We never share your information.</p>
                </form>
              </>
            ) : (
              <div className="pen-success">
                <div className="pen-success-icon">✨</div>
                <h3>You're In, {userName}!</h3>
                <div className="pen-success-badge">✓ Studio request received</div>
                <p style={{ marginBottom: 12 }}>
                  Our team will reach out within <strong>24 business hours</strong> to set up your
                  AI workspace and train your brand voice.
                </p>
                <div className="pen-next-box">
                  <div className="pen-next-title">What happens next?</div>
                  <p>📞 A quick call to understand your brand and goals</p>
                  <p>🧠 We train your brand voice model</p>
                  <p>🎨 You get your first AI-generated concepts to react to</p>
                </div>
                <p style={{ fontSize: 13, marginTop: 12 }}>
                  Questions?{' '}
                  <a href="mailto:info@Pavanltd.com" className="pen-success-mail">info@Pavanltd.com</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="pen-footer">
        <div className="pen-wrap pen-ftin">
          <div>
            <div className="pen-flogo">
              <LogoMark size={38} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span className="pen-flt">Pavan Enterprises Network</span>
                <span className="pen-flt-sub">AI Creative Studio</span>
              </div>
            </div>
            <p className="pen-fdesc">
              The AI creative studio for ambitious brands — image ads, video, UGC, product shots,
              blogs, and brand identity, generated in your voice.
            </p>
          </div>
          <div className="pen-fcol">
            <h4>Create</h4>
            <a href="#create">Image Ads</a>
            <a href="#create">AI Video Ads</a>
            <a href="#create">UGC-Style Ads</a>
            <a href="#create">Product Shots</a>
            <a href="#create">Blogs & Logos</a>
            <a href="#create">Brand Voice</a>
          </div>
          <div className="pen-fcol">
            <h4>Company</h4>
            <a href="mailto:info@Pavanltd.com">info@Pavanltd.com</a>
            <a href="#pen-register">Start Free</a>
            <a href="#how">How It Works</a>
            <a href="#why">Why PEN</a>
          </div>
        </div>
        <div className="pen-wrap pen-fbot">
          <p>© {new Date().getFullYear()} Pavan Enterprises Network. All rights reserved.</p>
          <p>Made for ambitious brands everywhere ✦</p>
        </div>
      </footer>

      {/* ── FLOATING BUTTON ── */}
      <div className="pen-fabs">
        <a href="mailto:info@Pavanltd.com" className="pen-fab" title="Email Us">✉️</a>
      </div>
    </div>
  );
}